import store from '@/store';
import Utils from '@/modules/Utils';
import { FloodSensor } from '@/modules/Types';

export function getMapIcon(device, isSelected, cabinetById) {
    if (device.class_name === 'virtual_controller'){
        // virtual devices can be marked as retrofit too, so the retrofit icon
        // must win over the virtual one
        return getRetrofitIcon(device, 'regular', isSelected) ||
            (isSelected ? 'selected_virtualOff' : 'virtualOff');
    }
    
    if (device['meta.category'].category === 'HUB') {
        return getCabinetIcon(device);
    }

    if ((device['meta.category'].category !== 'FIXTURE') || device.class_name === 'Lora_BLE_nRF52V3') {
        return getSpecialDeviceIcon(device);
    }

    // Retrofit is checked before the sys___active icons: a device out for
    // Retrofit while it happened to be in installation / maintenance / care
    // otherwise kept its old icon, even though every other consumer (info
    // window, tabs, isDeviceLive) already reports it as Retrofit.
    const retrofit_icon = getRetrofitIcon(device, 'regular', isSelected);
    if (retrofit_icon) return retrofit_icon;

    // sys___active icons are displayed regardless of cabinet configuration
    if (device.sys___active === -3)
        return isSelected ? 'selected_regularInInstallation' : 'regularInInstallation';
    if (device.sys___active === -1)
        return isSelected ? 'selected_regularInMaintenance' : 'regularInMaintenance';
    if (device.sys___active === -9)
        return isSelected ? 'selected_regularInCare' : 'regularInCare';

    const icon_type = (isSelected ? 'selected_' : '') + (device['meta.device'].fixture_icon ? device['meta.device'].fixture_icon.toLowerCase() : 'regular');
    // if device doesn't have sys___active related icon, it will be determined according to cabinet configuration
    const cabinet = getDeviceCabinet(device, cabinetById);
    if (cabinetHasVariableWindow(cabinet)) {
        return icon_type + getVariableTimeWindowIcon(device, cabinet);
    } else {
        return icon_type + get24_7Icon(device, cabinet);
    }
}

// Build an O(1) lookup map from cabinet_id → cabinet device. Pass into getMapIcon
// to avoid repeated Vuex getter traversals (Reports/commissionedCabinets) per call.
export function buildCabinetByIdMap() {
    const map = new Map();
    const cabinets = store.getters['Reports/commissionedCabinets'] || [];
    cabinets.forEach((cabinet) => {
        const id = cabinet['meta.device'] && cabinet['meta.device'].cabinet_id;
        if (id) map.set(id, cabinet);
    });
    return map;
}

function getCabinetIcon(device){
    if (device.class_name === 'virtual_cabinet')
        return getRetrofitIcon(device, 'cabinet') || 'virtualCabinet';

    // Retrofit wins over the sys___active icons, same as the fixture path above:
    // a cabinet out for Retrofit that happened to be in installation kept the
    // installation icon, while its badge, tabs and isDeviceLive all reported Retrofit.
    const retrofit_icon = getRetrofitIcon(device, 'cabinet');
    if (retrofit_icon) return retrofit_icon;

    if (device.sys___active === -3) return 'cabinetInInstallation';
    
    return device.sys___active !== 1 ?
            'regularCabinetFault'
            : isCabinetOn(device)
            ? 'regularCabinetOn'
            : 'regularCabinetOff';
}

// See-through mode: while on, retrofit devices render their real
// connectivity-derived icon instead of the retrofit one. Module-level so the
// store's own icon recomputes (computeIconKey) honour it too, not just the map.
let retrofit_icons_bypassed = false;

export function setRetrofitIconsBypassed(bypassed) {
    retrofit_icons_bypassed = !!bypassed;
}

export function hasRetrofitIconState(device) {
    if (!device) return false;
    const operation_state = Utils.getOperationState(device);
    return operation_state === 'retrofit' ||
        operation_state === 'partial_retrofit' ||
        Utils.isPendingVerification(device);
}

// retrofit / pending-verification icons override the connectivity-derived icons,
// since connectivity isn't meaningful while a device is out for retrofit
function getRetrofitIcon(device, family, isSelected) {
    if (retrofit_icons_bypassed) return null;

    const prefix = isSelected ? 'selected_' : '';
    const operation_state = Utils.getOperationState(device);
    if (operation_state === 'retrofit' || operation_state === 'partial_retrofit') {
        return family === 'cabinet' ? 'cabinetRetrofit' : prefix + 'regularRetrofit';
    }
    if (Utils.isPendingVerification(device)) {
        return family === 'cabinet' ? 'cabinetPendingVerification' : prefix + 'regularPendingVerification';
    }
    return null;
}

function cabinetHasVariableWindow(cabinet) {
    if (!cabinet) return false;
    const window = getCabinetWindow(cabinet);
    return window && window.start !== '00:00' && window.end !== '23:59';
}


function getVariableTimeWindowIcon(device, cabinet) {
    if (inTimeWindow(getCabinetWindow(cabinet))){
        return getInsideWindowIcon(device);
    } else {
        return getOutsideWindowIcon(device);
    }
}

function getInsideWindowIcon(device) {
    if (device.sys___connected === 0) {
        if (device.sys___active === 0) {
            if (device.disconnect_reason === 'communication_fault') {
                return deviceHasSchedule(device) ? (deviceScheduleOn(device) ? 'CommFaultOn' : 'CommFaultOff') : 'CommFaultOn';
            } else {
                return 'ElectricalFault';
            }
        } 
        if (device.sys___active === 1) {
            return deviceHasSchedule(device) ? (deviceScheduleOn(device) ? 'CommFaultOn' : 'CommFaultOff') : 'CommFaultOn';
        }
        return 'ElectricalFault';
    } 
    if(device.sys___connected === 1) {
        return device.power > 0 ? 'On' : 'Off';
    }

    return 'ElectricalFault';
}

function deviceHasSchedule(device) {
    return device['device.schedule'].schedule.length > 0;
}

function deviceScheduleOn(device) {
    if (device['device.schedule'].schedule.length === 0) return false;
    const schedule_type = device['device.schedule'].scheduler_type;
    if (schedule_type === 'cabinet_scheduler') {
        return scheduleIsActive(getDeviceCabinet(device));
    }

    return isGroupScheduleActive(device);
}

function isGroupScheduleActive(device) {
    const dayOfWeek = getDayOfWeek(getProjectTimezone())
    const today_curve = device['meta.schedule_curve'].open_schedule[dayOfWeek];
    return isCurveActive(today_curve);
}

function isCurveActive(curve) {
    const now = new Date();
    const currentMinutes = now.getUTCHours() * 60 + now.getUTCMinutes();

    return curve.some(schedule => {
        const { start_time_minutes, duration, power } = schedule;
        const end_time_minutes = start_time_minutes + duration;
        if (power === 0) return false;
        // schedule doesn't cross midnight
        if (end_time_minutes <= 1440) {
            return currentMinutes >= start_time_minutes && currentMinutes < end_time_minutes;
        }
        // schedule crosses midnight
        const adjusted_end_minutes = end_time_minutes % 1440;
        return (
            currentMinutes >= start_time_minutes || currentMinutes < adjusted_end_minutes
        );
    });
}

function getDayOfWeek(timezone) {
    const dateString = new Intl.DateTimeFormat('en-US', {
        timezone,
        weekday: 'long',
    }).format(new Date());

    const daysMap = {
        Sunday: 1,
        Monday: 2,
        Tuesday: 3,
        Wednesday: 4,
        Thursday: 5,
        Friday: 6,
        Saturday: 7
    }

    return daysMap[dateString];
}

function getOutsideWindowIcon(device) {
    if (device.sys___connected === 0) {
        if (device.sys___active === 0) {
            if(!(device.disconnect_reason === 'communication_fault' || device.disconnect_reason === 'electrical_fault' || device.disconnect_reason === 'active')) {
                return 'ElectricalFault';
            }
            return device.disconnect_reason === 'communication_fault' ? 'VoltageFault' : 'ElectricalFault';
        } 
        if (device.sys___active === 1) {
            return 'VoltageFault';
        }

        return 'ElectricalFault';
    } 
    if (device.sys___connected === 1) {
        return device.power > 0 ? 'On' : 'Off';
    }

    return 'ElectricalFault';
}

function get24_7Icon(device, cabinet) {
    if (device.sys___connected === 0) {
        if(device.sys___active === 1) {
            if (deviceHasSchedule(device)) {
                return deviceScheduleOn(device) ? 'CommFaultOn' : 'CommFaultOff';
            } else {
                return 'CommFaultOn';
            }
        } 
        if (device.sys___active === 0) {
            if (device.disconnect_reason === 'communication_fault') {
                if(deviceHasSchedule(device)) {
                    return deviceScheduleOn(device) ? 'CommFaultOn' : 'CommFaultOff';
                } else {
                    return 'CommFaultOn';
                }
            } 
            if (device.disconnect_reason === 'electrical_fault' || device.disconnect_reason === 'active') {
                return 'ElectricalFault';
            }

            return 'ElectricalFault';
        }

        return 'ElectricalFault';
    }
    if (device.sys___connected === 1) {
        return device.power > 0 ? 'On' : 'Off';
    }

    return 'ElectricalFault';
}

function inTimeWindow(window) {
    if (!window) return false;
    // Get current time in GMT
    const now = new Date();
    const gmtHour = now.getUTCHours();
    const gmtMinute = now.getUTCMinutes();
    const currentTime = gmtHour * 60 + gmtMinute;
    
    // Parse window times (already in GMT)
    const startHour = parseInt(window.start.split(':')[0]);
    const startMinute = parseInt(window.start.split(':')[1]);
    const endHour = parseInt(window.end.split(':')[0]);
    const endMinute = parseInt(window.end.split(':')[1]);
    
    const startTime = startHour * 60 + startMinute;
    const endTime = endHour * 60 + endMinute;
    if (startTime < endTime) {
        return currentTime >= startTime && currentTime <= endTime;
    } else {
        return currentTime >= startTime || currentTime <= endTime;
    }
}

function getCabinetWindow(cabinet) {
    let configObj = null;
    try {
        if (cabinet['services.config']) {
            configObj = cabinet['services.config'];
        }
    } catch (error) {
        console.error('error parsing cabinet config:', error);
    }
    return cabinet['services.config']?.alerts?.window || null;
}

function scheduleIsActive(cabinet) {
    if (!cabinet) return false;
    try {
        if (cabinet['meta.devices_schedule']) {
            return cabinet['meta.devices_schedule']?.active && inCabinetSchedule(cabinet['meta.devices_schedule']);
        }
    } catch (error) {
        console.error('error parsing schedule for cabinet', cabinet, ':', error);
    }  

    return false;
}

function inCabinetSchedule(schedule) {
    const window = {start: null, end: null};
    if (schedule.is_astronomical) {
        const dusk_dawn_data = store.state.Global.dusk_dawn_data;
        window.start = Utils.getAstronomicalTime(schedule.dusk, dusk_dawn_data.dusk_timestamp, dusk_dawn_data.dusk, 'Etc/UTC')
        window.end = Utils.getAstronomicalTime(schedule.dawn, dusk_dawn_data.dawn_timestamp, dusk_dawn_data.dawn, 'Etc/UTC')
    } else {
        window.start = schedule.on;
        window.end = schedule.off;
    }    
    return inTimeWindow(window);
}

// functions based on current project's (vue/react) capabilities

function getDeviceCabinet(device, cabinetById) {
    const cabinetId = device['meta.device'].cabinet_id ? device['meta.device'].cabinet_id : 'default';

    if (cabinetById) {
        return cabinetById.get(cabinetId) || (cabinetId !== 'default' ? cabinetById.get('default') : undefined);
    }

    const cabinet = store.getters['Reports/commissionedCabinets']
        .find((cabinet) => cabinet['meta.device'].cabinet_id === cabinetId);

    if (!cabinet && cabinetId !== 'default') {
        return store.getters['Reports/commissionedCabinets']
        .find((cabinet) => cabinet['meta.device'].cabinet_id === 'default');
    }

    return cabinet;
}

function getProjectTimezone(){
    return store.state.Global.timezone;
}

function isCabinetOn(device) {
    return store.state.ProjectStreams.digital_input_by_cabinet.get(device.id) &&
            store.state.ProjectStreams.digital_input_by_cabinet.get(device.id)['15'];
}

function getSpecialDeviceIcon(device) {
    // Non-fixture device types (sensors, cameras, barriers, water meters, lora
    // children, ...) can be marked as Retrofit too, so the retrofit icon has to
    // win over their type-specific icons as well.
    const retrofit_icon = getRetrofitIcon(device, 'regular');
    if (retrofit_icon) return retrofit_icon;

    if (device['meta.category'].category === 'RADAR')
        return 'radarOk';

    if (Utils.hasFloodSensorClass(device['meta.category'].category)) {
        return device[FloodSensor.BOTTOM_FLOAT_TRIGGER] === 1 && device[FloodSensor.TOP_FLOAT_TRIGGER] === 1 && device[FloodSensor.ULRASONIC_SENSOR_TRIGGER] === 1
            ? 'floodSensorAlert'
              : device[FloodSensor.ULRASONIC_SENSOR_TRIGGER] === 1
                ? 'topSensorAlert'
                : device[FloodSensor.BOTTOM_FLOAT_TRIGGER] === 1 || device[FloodSensor.TOP_FLOAT_TRIGGER] === 1
                  ? 'bottomSensorAlert'
                  : 'floodSensor';
      }

      if (Utils.hasMotionSensorClass(device.class_name)) {
        return 'sensorOk';
      }

      if (Utils.hasBarrierClass(device['meta.category'].category)) {
        return device.power > 0
          ? 'alertBarrier'
          : 'openBarrier';
      }

      if (Utils.hasVideoClass(device.class_name))
        return device.sys___active === 0
          ? 'cameraFault'
          : 'cameraOk';

      if (Utils.hasWaterMeterClass(device.class_name)) {
        return device.sys___active ? 'waterMeter' : 'waterMeterFault';
      }

      if (Utils.hasWeatherStationClass(device.class_name)) {
        return 'weatherStation';
      }

      // If device has parent - for lora devices
      if (device.parent_id) {
        const devices = store.getters['Reports/commissionedDevices'];
        const parent = devices.find((v) => v.id === device.parent_id);
        if (parent) {
          const parentOnline = parent.sys___connected !== 0;
          // check if parent is offline
          if (!parentOnline) {
            return parent.power > 0
              ? 'yellowRedLine'
              : device.power > 0
              ? 'yellowRedLine'
              : 'greyRedLine';
          }
        }
      }

      return 'regularOff';
}